import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/utils";
import {
  useFailReasonBreakdown,
  useFailReasonDetails,
} from "@/hooks/use-fail-reason-stats";
import type { DashboardStatsQuery, FailReasonBreakdownStats } from "@/types";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";

type Props = {
  statsQuery: DashboardStatsQuery;
  refetchKey: number;
  onLoadingChange?: (loading: boolean) => void;
  /** When provided, skip breakdown fetch (dashboard bundle supplies this). */
  breakdownFromBundle?: FailReasonBreakdownStats | null;
  bundleLoading?: boolean;
  /** Refresh breakdown (parent bundle refetch when using bundle). */
  onBundleRefresh?: () => void;
};

const CHART_FILLS = [
  "hsl(var(--chart-1))",
  "hsl(var(--chart-2))",
  "#8b5cf6",
  "hsl(var(--chart-3))",
  "#3b82f6",
  "#a855f7",
  "#0ea5e9",
  "#d946ef",
];

function displayFailReason(s: string): string {
  return s.replace(/_/g, " ");
}

export function FailReasonChart({
  statsQuery,
  refetchKey,
  onLoadingChange,
  breakdownFromBundle,
  bundleLoading,
  onBundleRefresh,
}: Props) {
  const useBundle = breakdownFromBundle !== undefined;
  const {
    data: fetched,
    loading: fetchLoading,
    error,
    refresh,
  } = useFailReasonBreakdown(statsQuery, {
    refetchKey,
    enabled: !useBundle,
  });
  const data = useBundle ? breakdownFromBundle : fetched;
  const loading = useBundle ? Boolean(bundleLoading) : fetchLoading;
  const doRefresh = useBundle ? onBundleRefresh ?? (() => {}) : refresh;
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);

  const {
    data: detailsData,
    loading: detailsLoading,
    error: detailsError,
  } = useFailReasonDetails(statsQuery, selectedReason, {
    enabled: Boolean(sheetOpen && selectedReason?.trim()),
    refetchKey,
  });

  const rows = useMemo(() => data?.series ?? [], [data?.series]);

  useEffect(() => {
    onLoadingChange?.(loading);
  }, [loading, onLoadingChange]);

  const isInitial = loading && !data;
  const isUpdating = loading && Boolean(data);

  const onBarPick = useCallback(
    (failReason: string) => {
      setSelectedReason(failReason);
      setSheetOpen(true);
    },
    [],
  );

  return (
    <section
      className="rounded-xl border border-border bg-card shadow-sm"
      aria-label="Fail reason distribution"
    >
      <div className="border-b border-rose-500/15 bg-gradient-to-br from-rose-500/[0.08] via-card to-amber-500/[0.06] px-4 py-3 sm:px-5 sm:py-4">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-rose-600/90 dark:text-rose-300/80">
          Failures
        </p>
        <h2 className="mt-1.5 text-lg font-semibold leading-tight tracking-tight text-foreground sm:text-xl">
          <span className="text-rose-600/95 dark:text-rose-400">Fail reason</span>
          <span className="text-muted-foreground"> </span>
          <span className="text-amber-700/90 dark:text-amber-300/90">by call</span>
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Each bar counts <span className="font-medium">history rows</span> with a non-empty{" "}
          <span className="font-mono text-[11px]">fail_reason</span> (after the same case merge as
          Path analytics). Click a bar for <span className="font-mono text-[11px]">fail_details</span>{" "}
          breakdown.
        </p>
        {data?.capped ? (
          <p className="mt-2 text-xs text-amber-700/90 dark:text-amber-400/90">
            No case filters: using the {data.cap} most recent cases. Add filters to scope a
            different set.
          </p>
        ) : null}
      </div>

      {error ? (
        <p className="px-4 py-2 text-sm text-destructive sm:px-5" role="alert">
          {error}
        </p>
      ) : null}

      <div className="space-y-3 px-4 pb-5 sm:px-5 sm:pb-6">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          {(
            [
              {
                label: "Rows w/ fail_reason",
                value: data?.rows_with_fail_reason,
                className:
                  "bg-rose-500/10 text-rose-900 dark:text-rose-200 border-rose-500/20",
              },
              {
                label: "Documents",
                value: data?.scanned_case_count,
                className: "bg-slate-500/8 text-foreground border-border",
              },
              {
                label: "Logical",
                value: data?.grouped_case_count ?? data?.scanned_case_count,
                className: "bg-slate-500/8 text-foreground border-border",
              },
            ] as const
          ).map((row) => (
            <span
              key={row.label}
              className={cn(
                "inline-flex items-baseline gap-1.5 rounded-md border px-2.5 py-1",
                row.className,
              )}
            >
              <span className="text-xs text-muted-foreground">{row.label}</span>
              <span className="font-semibold tabular-nums text-foreground">
                {row.value ?? "—"}
              </span>
            </span>
          ))}
          {isUpdating ? (
            <span className="text-xs text-muted-foreground" aria-live="polite">
              Updating…
            </span>
          ) : null}
          <button
            type="button"
            className="ml-auto text-xs font-medium text-rose-600 hover:underline dark:text-rose-400"
            onClick={() => void doRefresh()}
          >
            Refresh
          </button>
        </div>

        {isInitial ? (
          <div className="space-y-2 py-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : rows.length === 0 && !loading ? (
          <p className="py-10 text-center text-sm text-muted-foreground max-w-md mx-auto">
            No call rows with a fail reason in this filtered set, or the merged histories are
            empty.
          </p>
        ) : (
          <div
            className={cn(
              "relative w-full min-w-0 pl-0 pr-1 sm:pr-0",
              isUpdating && "opacity-60",
            )}
          >
            {isUpdating ? (
              <div
                className="pointer-events-none absolute inset-0 z-10 flex items-start justify-end pt-2 pr-2"
                aria-live="polite"
              >
                <span className="rounded-md border border-border bg-background/90 px-2 py-1 text-xs text-muted-foreground shadow-sm backdrop-blur">
                  Loading…
                </span>
              </div>
            ) : null}
            <div className="h-80 w-full min-h-[18rem] rounded-lg border border-rose-500/10 bg-gradient-to-b from-rose-500/[0.04] to-transparent px-1 pt-2 pb-2 sm:px-2 sm:pb-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={rows}
                  margin={{ top: 8, right: 8, left: 0, bottom: 56 }}
                  barCategoryGap="14%"
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--chart-grid) / 0.45)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="label"
                    type="category"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                    tickLine={false}
                    axisLine={{ stroke: "hsl(var(--border))" }}
                    interval={0}
                    height={64}
                    angle={-32}
                    textAnchor="end"
                    tickFormatter={(v) =>
                      v.length > 20 ? `${String(v).slice(0, 18)}…` : String(v)
                    }
                    label={{
                      value: "Fail reason (enum)",
                      position: "insideBottom",
                      offset: -20,
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 11,
                    }}
                  />
                  <YAxis
                    className="text-xs"
                    allowDecimals={false}
                    width={40}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    label={{
                      value: "Rows",
                      angle: -90,
                      position: "insideLeft",
                      fill: "hsl(var(--muted-foreground))",
                      fontSize: 11,
                    }}
                  />
                  <RechartsTooltip
                    cursor={{ fill: "hsl(var(--muted) / 0.15)" }}
                    content={({ active, payload }) => {
                      if (!active || !payload?.[0]) return null;
                      const p = payload[0].payload as (typeof rows)[0];
                      return (
                        <div
                          className={cn(
                            "rounded-lg border border-rose-500/20 bg-popover/95 px-3 py-2 text-sm shadow-lg backdrop-blur-sm",
                          )}
                        >
                          <p className="font-mono text-xs text-foreground">
                            {p.fail_reason}
                          </p>
                          <p className="text-muted-foreground">Rows: {p.count}</p>
                          <p className="text-[11px] text-primary">Click to open details</p>
                        </div>
                      );
                    }}
                  />
                  <Bar
                    dataKey="count"
                    name="Rows"
                    radius={[4, 4, 0, 0]}
                    className="cursor-pointer outline-none"
                    onClick={(d: unknown) => {
                      if (d && typeof d === "object") {
                        const o = d as {
                          payload?: (typeof rows)[number];
                          fail_reason?: string;
                        };
                        const fr = o.payload?.fail_reason ?? o.fail_reason;
                        if (fr) onBarPick(fr);
                      }
                    }}
                  >
                    {rows.map((entry, i) => (
                      <Cell
                        key={entry.fail_reason}
                        fill={CHART_FILLS[i % CHART_FILLS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <Sheet
        open={sheetOpen}
        onOpenChange={(o) => {
          setSheetOpen(o);
          if (!o) setSelectedReason(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto"
          aria-describedby={undefined}
        >
          <SheetHeader>
            <SheetTitle className="pr-8 text-left">
              {selectedReason
                ? `Fail details — ${displayFailReason(selectedReason)}`
                : "Fail details"}
            </SheetTitle>
            {selectedReason ? (
              <SheetDescription>
                Counts of each distinct fail_details string for history rows with this fail_reason
                (same filters as the chart).
              </SheetDescription>
            ) : null}
          </SheetHeader>

          <div className="mt-4">
            {detailsError ? (
              <p className="text-sm text-destructive" role="alert">
                {detailsError}
              </p>
            ) : detailsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : detailsData ? (
              <>
                <p className="text-xs text-muted-foreground">
                  {detailsData.rows_in_reason} row{detailsData.rows_in_reason === 1 ? "" : "s"}{" "}
                  for this reason
                  {detailsData.capped ? ` · Capped at ${detailsData.cap} case documents` : ""}
                </p>
                <div className="mt-3 max-h-[min(60vh,32rem)] overflow-y-auto rounded-md border border-border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[50%]">fail_details</TableHead>
                        <TableHead className="w-[20%] text-right">Count</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {Object.entries(detailsData.by_fail_details).map(
                        ([detail, count]) => (
                          <TableRow key={detail}>
                            <TableCell className="whitespace-pre-wrap break-words align-top text-sm">
                              {detail}
                            </TableCell>
                            <TableCell className="text-right font-semibold tabular-nums align-top">
                              {count}
                            </TableCell>
                          </TableRow>
                        ),
                      )}
                    </TableBody>
                  </Table>
                </div>
                {Object.keys(detailsData.by_fail_details).length === 0 ? (
                  <p className="text-sm text-muted-foreground">No rows for this reason.</p>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Select a bar to load details.</p>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
}
